import {
	ANGLE,
	CHANNEL,
	HEX_BAR,
	PLATE,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
} from '../../config/constants';

/**
 * Shared "end-on" cross-section preview for a material length's shape.
 *
 * Originally lived as module-private helpers inside `MaterialLengthsTable`
 * (the small Preview column icon). Extracted so other views — e.g. the
 * inventory slot front-view diagram — can reuse the exact same shape
 * rendering/coloring logic. `MaterialShapePreview` only returns SVG
 * primitives (no wrapping `<svg>`), so callers drop it into whatever
 * viewBox/coordinate-system they own.
 */

export const getShapeName = (record: any) => record.material_shape?.display_name || '';

export const getMaterialPreviewColor = (record: any) => {
	const materialText = [
		record.material_type?.display_name,
		record.material_type?.material_category?.display_name,
	].join(' ').toLowerCase();

	if (materialText.includes('aluminum')) return '#d9d9d9';
	if (materialText.includes('stainless')) return '#9fb4c8';
	if (materialText.includes('steel')) return '#5f6368';
	return '#8c8c8c';
};

export const getPreviewSize = (record: any) => {
	const shapeName = getShapeName(record);
	const widthSource = [ROUND_BAR, ROUND_TUBE].includes(shapeName)
		? Number(record.outer_diameter)
		: Number(record.width);
	const heightSource = [ROUND_BAR, ROUND_TUBE].includes(shapeName)
		? Number(record.outer_diameter)
		: Number(record.height);
	const width = Number.isFinite(widthSource) && widthSource > 0 ? widthSource : 1;
	const height = Number.isFinite(heightSource) && heightSource > 0 ? heightSource : 1;
	const ratio = Math.max(0.35, Math.min(width / height, 2.8));

	if (ratio >= 1) {
		return {
			width: Math.min(72, 28 * ratio),
			height: Math.max(18, 28 / Math.sqrt(ratio)),
		};
	}

	return {
		width: Math.max(22, 28 * Math.sqrt(ratio)),
		height: Math.min(32, 28 / ratio),
	};
};

export const MaterialShapePreview = ({ record }: { record: any }) => {
	const shapeName = getShapeName(record);
	const color = getMaterialPreviewColor(record);
	const size = getPreviewSize(record);
	const x = (100 - size.width) / 2;
	const y = (44 - size.height) / 2;
	const stroke = '#3f3f3f';

	if (shapeName === ROUND_BAR) {
		return (
			<ellipse
				cx={50}
				cy={22}
				rx={size.width / 2}
				ry={size.height / 2}
				fill={color}
				stroke={stroke}
				strokeWidth={1}
			/>
		);
	}

	if (shapeName === ROUND_TUBE) {
		const innerRadius = Math.max(4, Math.min(size.width, size.height) * 0.28);
		return (
			<>
				<ellipse
					cx={50}
					cy={22}
					rx={size.width / 2}
					ry={size.height / 2}
					fill={color}
					stroke={stroke}
					strokeWidth={1}
				/>
				<ellipse
					cx={50}
					cy={22}
					rx={innerRadius}
					ry={innerRadius}
					fill="#fff"
					stroke={stroke}
					strokeWidth={1}
				/>
			</>
		);
	}

	if (shapeName === HEX_BAR) {
		const left = x;
		const right = x + size.width;
		const top = y;
		const bottom = y + size.height;
		const midY = y + size.height / 2;
		const inset = size.width * 0.22;
		return (
			<polygon
				points={`${left + inset},${top} ${right - inset},${top} ${right},${midY} ${right - inset},${bottom} ${left + inset},${bottom} ${left},${midY}`}
				fill={color}
				stroke={stroke}
				strokeWidth={1}
			/>
		);
	}

	if (shapeName === ANGLE) {
		const thickness = Math.max(7, Math.min(size.width, size.height) * 0.28);
		return (
			<path
				d={`M ${x} ${y} H ${x + size.width} V ${y + thickness} H ${x + thickness} V ${y + size.height} H ${x} Z`}
				fill={color}
				stroke={stroke}
				strokeWidth={1}
			/>
		);
	}

	if (shapeName === CHANNEL) {
		const thickness = Math.max(6, Math.min(size.width, size.height) * 0.22);
		return (
			<path
				d={`M ${x} ${y} H ${x + size.width} V ${y + thickness} H ${x + thickness} V ${y + size.height - thickness} H ${x + size.width} V ${y + size.height} H ${x} Z`}
				fill={color}
				stroke={stroke}
				strokeWidth={1}
			/>
		);
	}

	if (shapeName === RECTANGLE_TUBE) {
		const inset = Math.max(5, Math.min(size.width, size.height) * 0.18);
		return (
			<>
				<rect
					x={x}
					y={y}
					width={size.width}
					height={size.height}
					rx={2}
					fill={color}
					stroke={stroke}
					strokeWidth={1}
				/>
				<rect
					x={x + inset}
					y={y + inset}
					width={Math.max(4, size.width - inset * 2)}
					height={Math.max(4, size.height - inset * 2)}
					rx={1}
					fill="#fff"
					stroke={stroke}
					strokeWidth={1}
				/>
			</>
		);
	}

	return (
		<rect
			x={x}
			y={y}
			width={size.width}
			height={size.height}
			rx={shapeName === PLATE ? 1 : 2}
			fill={color}
			stroke={stroke}
			strokeWidth={1}
		/>
	);
};
